import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhone, normalizePhone, phoneDisplayFormat } from '@shared/validation';
import { useMutation } from '@tanstack/react-query';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

import { apiRequest } from '@/lib/queryClient';
import { useLocation } from 'wouter';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { useWebinarTimes } from '@/lib/webinarCalculations';

// Base form schema without webinarTime
const baseFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().refine(val => val.trim() !== '' && isValidPhone(val), {
    message: "Please enter a valid US phone number (e.g. 555-123-4567)",
  }),
  smsConsent: z.boolean().refine(val => val === true, {
    message: "You must agree to receive SMS updates to register for the webinar",
  }),
});

// Regular form schema with required webinarTime
const formSchema = baseFormSchema.extend({
  webinarTime: z.date({
    required_error: "Please select a webinar time",
  }),
});

// On-demand form schema with optional webinarTime
const onDemandFormSchema = baseFormSchema.extend({
  webinarTime: z.date().optional(),
});

type FormValues = z.infer<typeof formSchema>;
type OnDemandFormValues = z.infer<typeof onDemandFormSchema>;

interface SimpleRegistrationModalProps {
  selectedWebinarTime?: Date | null;
}

export const SimpleRegistrationModal = ({ selectedWebinarTime: propsSelectedTime }: SimpleRegistrationModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [storedTime] = useLocalStorage<Date | null>('selectedWebinarTime', null);
  const [_, setRegistration] = useLocalStorage('webinarRegistration', null);
  const { webinarTimes } = useWebinarTimes();
  
  // Check if on-demand mode is active
  const urlParams = new URLSearchParams(window.location.search);
  const isOnDemandMode = urlParams.get('ondemand') === 'yes';
  
  // Use time from localStorage if available
  const selectedWebinarTime = propsSelectedTime || storedTime;
  
  const form = useForm<FormValues | OnDemandFormValues>({
    resolver: zodResolver(isOnDemandMode ? onDemandFormSchema : formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      smsConsent: false, // User must actively agree to SMS updates
      webinarTime: isOnDemandMode ? new Date() : (selectedWebinarTime || undefined),
    },
  });
  
  // When selectedWebinarTime changes, update the form value
  useEffect(() => {
    if (selectedWebinarTime && selectedWebinarTime instanceof Date) {
      form.setValue('webinarTime', selectedWebinarTime);
    }
  }, [selectedWebinarTime]);
  
  // Add the global open function
  useEffect(() => {
    // Define a global function to open the modal
    window.openRegistrationModal = () => {
      console.log("Opening registration modal", new Date().toISOString());
      setIsOpen(true);
    };
    
    // Define a function to open the modal with a specific time
    window.openRegistrationModalWithTime = (time: Date) => {
      console.log("Opening registration modal with time:", time);
      
      // Set the time in the form
      form.setValue('webinarTime', time);
      
      // Open the modal
      setIsOpen(true);
    };
    
    return () => {
      // Clean up
      if (window.openRegistrationModal) {
        window.openRegistrationModal = undefined as any;
      }
      if (window.openRegistrationModalWithTime) {
        window.openRegistrationModalWithTime = undefined as any;
      }
    };
  }, [form]);
  
  const registerMutation = useMutation({
    mutationFn: async (values: FormValues | OnDemandFormValues) => {
      // In on-demand mode, use current time as webinar time if not provided
      let webinarTime = values.webinarTime;
      if (isOnDemandMode && (!webinarTime || !(webinarTime instanceof Date))) {
        webinarTime = new Date();
      } else if (!isOnDemandMode && (!webinarTime || !(webinarTime instanceof Date))) {
        throw new Error("Please select a valid webinar time");
      }
      
      const trackingData = JSON.parse(sessionStorage.getItem('webinar_tracking') || '{}');
      const data = {
        ...values,
        webinarTime: webinarTime!.toISOString(),
        webinarLink: `${window.location.origin}/${isOnDemandMode ? '?ondemand=yes' : ''}`,
        utmSource: trackingData.utm_source || '',
        utmMedium: trackingData.utm_medium || '',
        utmCampaign: trackingData.utm_campaign || '',
        utmTerm: trackingData.utm_term || '',
        utmContent: trackingData.utm_content || '',
        gclid: trackingData.gclid || '',
        fbclid: trackingData.fbclid || '',
        fbp: trackingData.fbp || '',
        fbc: trackingData.fbc || '',
        referrer: trackingData.referrer || '',
        landingPage: trackingData.landing_page || '',
      };
      
      const res = await apiRequest('POST', '/api/webinar/register', data);
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Registration successful!",
        description: "You will receive a confirmation email shortly.",
      });
      
      // Save registration data to localStorage
      setRegistration(data);
      
      // Save the selected webinar time for countdown
      const formValues = form.getValues();
      if (formValues.webinarTime) {
        localStorage.setItem('selectedWebinarTime', formValues.webinarTime.toISOString());
      }
      
      // Close dialog
      setIsOpen(false);
      
      // Check if we're in test mode and add parameter
      const currentUrl = new URL(window.location.href);
      const isTestMode = currentUrl.searchParams.get('test-mode') === '1';
      
      // Use existing isOnDemandMode variable instead of re-parsing
      console.log("Redirect logic - isOnDemandMode:", isOnDemandMode, "isTestMode:", isTestMode, "data.id:", data.id);
      
      // Redirect ALL registrants (scheduled, test, on-demand) to the webinar
      // holding room. The pre-start countdown handles the wait until start time.
      let redirectUrl = '/webinar';
      const params = new URLSearchParams();
      
      if (isTestMode) params.set('test-mode', '1');
      if (isOnDemandMode) params.set('ondemand', 'yes');
      
      // Always add registration ID to ensure unique URL for proper navigation
      if (data.id) {
        params.set('r', data.id.toString());
      }
      
      // Append the canonical &time= param so the URL matches the SMS/email
      // link format (/webinar?r={id}&time={encoded ISO}). Prefer the webinar
      // time returned by the server (the registration's stored time); fall back
      // to the form value used for the localStorage write above.
      let webinarTimeForUrl: Date | null = null;
      if (data.webinarTime) {
        const parsed = new Date(data.webinarTime);
        if (!isNaN(parsed.getTime())) {
          webinarTimeForUrl = parsed;
        }
      }
      if (!webinarTimeForUrl && formValues.webinarTime instanceof Date && !isNaN(formValues.webinarTime.getTime())) {
        webinarTimeForUrl = formValues.webinarTime;
      }
      if (webinarTimeForUrl) {
        params.set('time', webinarTimeForUrl.toISOString());
      }
      
      if (params.toString()) {
        redirectUrl += '?' + params.toString();
      }
      
      console.log("Redirecting to:", redirectUrl);
      setLocation(redirectUrl);
    },
    onError: (error) => {
      toast({
        title: "Registration failed",
        description: error instanceof Error ? error.message : "Please try again later",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: FormValues | OnDemandFormValues) => {
    console.log("Form submitted with values:", values);
    registerMutation.mutate(values);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setIsOpen(false)}>
      <div className="relative w-full max-w-xl bg-white rounded-lg shadow-xl overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <button 
            className="absolute right-2 top-2 flex items-center justify-center w-11 h-11 text-gray-500 hover:text-gray-900 active:text-gray-700 rounded-md"
            onClick={() => setIsOpen(false)}
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </button>
          
          <div className="mb-6 text-center">
            <h2 className="text-xl md:text-2xl font-bold">Reserve Your Spot Now</h2>
            <p className="text-gray-600 mt-2">
              Complete this form to register for our free estate planning webinar.
            </p>
          </div>
            
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {!isOnDemandMode && (
                <FormField
                  control={form.control}
                  name="webinarTime"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="font-medium">
                        Select Webinar Date & Time <span className="text-red-500">*</span>
                      </FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(new Date(value))}
                        defaultValue={field.value instanceof Date ? field.value.toISOString() : undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a webinar time" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {webinarTimes.map((time, index) => (
                            <SelectItem key={index} value={time.toISOString()}>
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">
                                  {new Intl.DateTimeFormat('en-US', {
                                    weekday: 'short',
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric',
                                    timeZone: 'America/New_York'
                                  }).format(time)}
                                </span>
                                <span className="text-gray-500">at</span>
                                <span className="text-primary">
                                  {new Intl.DateTimeFormat('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                    timeZone: 'America/New_York'
                                  }).format(time)}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choose from available webinar sessions. All times are in Eastern Time (ET).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-medium">
                        First Name <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="First name" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-medium">
                        Last Name <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Last name" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium">
                      Email Address <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input 
                        type="email"
                        placeholder="Your email address" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium">
                      Phone Number <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input 
                        type="tel"
                        placeholder="(555) 123-4567" 
                        {...field}
                        onBlur={() => {
                          const normalized = normalizePhone(field.value);
                          if (normalized) {
                            field.onChange(phoneDisplayFormat(normalized));
                          }
                          field.onBlur();
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {/* Required SMS consent checkbox */}
              <FormField
                control={form.control}
                name="smsConsent"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox 
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-medium">
                        Send Me Reminder SMS Text Messages <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormDescription className="text-xs text-gray-600">
                        You'll receive reminder messages about the webinar
                      </FormDescription>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
              

              
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={registerMutation.isPending}
                >
                  {registerMutation.isPending ? (
                    <span className="flex items-center">
                      <i className="fas fa-spinner fa-spin mr-2"></i> Registering...
                    </span>
                  ) : (
                    "Register Now"
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-center text-gray-600 mt-4">
                By registering, you agree to our <a href="#" className="text-primary hover:underline">Terms of Service</a> and <a href="#" className="text-primary hover:underline">Privacy Policy</a>.
              </p>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

// Add this to the global window object
declare global {
  interface Window {
    openRegistrationModal: () => void;
    openRegistrationModalWithTime: (time: Date) => void;
  }
}

// Export both named and default for flexibility
export default SimpleRegistrationModal;